// Sales + Purchase Drill-Down Dashboard (CPOS-only).
//
// Hierarchy: Overall → Category → State → City → Store → Invoice → Detail.
// Each frame in `navStack` carries the level + the inherited constraints; the
// fetcher below reads the active frame and pulls the corresponding aggregate /
// invoice list / invoice detail. Filters (search + date range) apply to every
// level.

import React, { useCallback, useEffect, useState } from "react";
import { ChevronRight, ArrowLeft, AlertTriangle, Loader2 } from "lucide-react";
import InnerLayout from "../../components/layouts/inner-layout";
import { isAdmin } from "../../redux/config";
import {
  fetchAggregate,
  fetchInvoices,
  fetchInvoiceDetail,
  resolveSearch,
} from "../../redux/apis/sales-drill-down-api";
import { defaultRange } from "./utils";
import FiltersBar from "./components/filters-bar";
import OverallCards from "./components/overall-cards";
import GroupGrid from "./components/group-grid";
import InvoiceTable from "./components/invoice-table";
import InvoiceDetail from "./components/invoice-detail";
import InsightsPanel from "./components/insights-panel";

// Level transitions: clicking a row at level X drills to level NEXT[X].
const NEXT_LEVEL = {
  overall: "category",
  category: "state",
  state: "city",
  city: "store",
};

const ROOT_FRAME = { type: "level", level: "overall", constraints: {}, label: "Overall" };

// Build a full nav-stack from a search-resolver match so the user lands
// directly on the deepest matching level with the breadcrumb chain
// pre-populated. The chain mirrors the level hierarchy:
//   Overall → Category → State → City → Store
// Each frame inherits constraints from its ancestors.
function buildNavStackFromMatch(match) {
  if (!match || !match.level) return [ROOT_FRAME];
  const { level, chain = {} } = match;
  const stack = [ROOT_FRAME];
  // Always push Categories as the first drill (its label matches what the
  // user sees clicking the overall card).
  stack.push({
    type: "level",
    level: "category",
    constraints: {},
    label: "Categories",
  });
  if (level === "category") return stack;

  stack.push({
    type: "level",
    level: "state",
    constraints: { category: chain.category },
    label: chain.category,
  });
  if (level === "state") return stack;

  stack.push({
    type: "level",
    level: "city",
    constraints: { category: chain.category, state: chain.state },
    label: chain.state,
  });
  if (level === "city") return stack;

  stack.push({
    type: "level",
    level: "store",
    constraints: { category: chain.category, state: chain.state, city: chain.city },
    label: chain.city,
  });
  // level === "store" → leave the user on the store grid (filtered by the
  // search term so the matching store is the only row visible).
  return stack;
}

export default function SalesDrillDownDashboard() {
  const [navStack, setNavStack] = useState([ROOT_FRAME]);
  const [filters, setFilters] = useState(() => {
    const r = defaultRange();
    // `item` is the product filter (item code): set when a search term
    // doesn't resolve to a store scope. It persists across drills so the
    // whole hierarchy — cards, grids, bill list — narrows to that product.
    return { search: "", item: "", from: r.from, to: r.to };
  });

  const frame = navStack[navStack.length - 1];

  // ---------- navigation ----------
  // Reset state to loading-with-no-data on every nav action so the body
  // never renders stale-shape data during the gap between the navStack
  // changing and the next fetch resolving (the gap is one render tick —
  // long enough for accessing data.invoices.length to crash).
  const resetState = () => setState({ loading: true, error: null, data: null });
  const push = useCallback((next) => {
    resetState();
    setNavStack((s) => [...s, next]);
  }, []);
  const popTo = useCallback((idx) => {
    resetState();
    setNavStack((s) => s.slice(0, idx + 1));
  }, []);
  const back = useCallback(() => {
    resetState();
    setNavStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);

  // ---------- data fetching ----------
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const reload = useCallback(async () => {
    setState({ loading: true, error: null, data: null });
    try {
      let data;
      if (frame.type === "level") {
        data = await fetchAggregate({
          level: frame.level,
          constraints: frame.constraints,
          from: filters.from,
          to: filters.to,
          search: filters.search,
          item: filters.item,
        });
      } else if (frame.type === "invoices") {
        data = await fetchInvoices({
          store_id: frame.constraints.store_id,
          metric: frame.metric,
          from: filters.from,
          to: filters.to,
          search: filters.search,
          item: filters.item,
        });
      } else if (frame.type === "invoiceDetail") {
        data = await fetchInvoiceDetail({
          id: frame.invoice_id,
          metric: frame.metric,
        });
      }
      setState({ loading: false, error: null, data });
    } catch (err) {
      setState({ loading: false, error: err, data: null });
    }
  }, [frame, filters]);
  useEffect(() => {
    reload();
  }, [reload]);

  // Smart-search auto-navigation:
  // When the cashier types a store code / city / state / category that
  // EXACTLY matches a known scope, jump straight to that level with the
  // breadcrumb chain pre-built — saves the click-through. Free-text terms
  // (item name, customer name, partial matches) return no resolver match
  // and just apply as a filter at the current level.
  //
  // Skipped when the user is already inside an invoices / invoiceDetail
  // frame so we don't yank them out of a list mid-read.
  useEffect(() => {
    const term = (filters.search || "").trim();
    if (!term) return;
    if (frame.type !== "level") return;
    let cancelled = false;
    resolveSearch(term)
      .then((res) => {
        if (cancelled) return;
        if (!res || !res.match) {
          // Not a store scope — treat the term as an ITEM CODE filter. It
          // moves from the search box into the item chip so every level
          // (cards, grids, bill lists) narrows to that product until cleared.
          setFilters((f) => ({ ...f, search: "", item: term }));
          return;
        }
        const next = buildNavStackFromMatch(res.match);
        // Avoid a redundant setState that would re-trigger a fetch.
        const sameStack =
          next.length === navStack.length &&
          next.every((f, i) =>
            f.type === navStack[i].type &&
            f.level === navStack[i].level &&
            f.label === navStack[i].label
          );
        if (sameStack) return;
        setNavStack(next);
        // CRITICAL: clear the search input once navigation lands. The term
        // has done its job (jumping us to the right scope) — keeping it in
        // the input would then apply as a filter on the invoice list and
        // produce 0 hits (the store code never appears in invoice numbers,
        // customer names, or item names). Users who want a finer filter at
        // the destination level can type a new term there.
        setFilters((f) => ({ ...f, search: "" }));
      })
      .catch(() => {
        // Silent — fall back to filter-only behavior.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  // ---------- drill handlers ----------
  const drillFromRow = (row) => {
    const currentLevel = frame.type === "level" ? frame.level : "overall";
    const nextLevel = NEXT_LEVEL[currentLevel];
    if (!nextLevel) return; // leaf reached
    const newConstraints = { ...frame.constraints };
    // Map the row's key into the right constraint slot for the NEW level.
    if (currentLevel === "overall") {
      // From overall, the click is on the big stat cards (handled separately);
      // group rows aren't possible at this level.
      return;
    }
    if (currentLevel === "category") newConstraints.category = row.key;
    if (currentLevel === "state") newConstraints.state = row.key;
    if (currentLevel === "city") newConstraints.city = row.key;
    if (currentLevel === "store") {
      // Store-level rows go to the invoices view, not "next level" — handled
      // by openInvoices below.
      return;
    }
    push({
      type: "level",
      level: nextLevel,
      constraints: newConstraints,
      label: row.label || row.key,
    });
  };
  const openInvoices = (row, metric) => {
    push({
      type: "invoices",
      metric,
      constraints: { ...frame.constraints, store_id: row.key },
      label: `${row.code || row.key} · ${metric === "sales" ? "Sales" : "Purchase"}`,
    });
  };
  const openInvoiceDetail = (inv) => {
    push({
      type: "invoiceDetail",
      metric: frame.metric,
      invoice_id: inv.invoice_id,
      constraints: frame.constraints,
      label: inv.invoice_no || inv.invoice_id,
    });
  };
  const drillFromOverallCard = (kind) => {
    push({ type: "level", level: "category", constraints: {}, label: "Categories" });
  };

  // ---------- guard for non-CPOS ----------
  // Reuses the existing isAdmin flag (true on CPOS, false on POS).
  if (!isAdmin) {
    return (
      <InnerLayout title="Sales Breakdown">
        <NotForPos />
      </InnerLayout>
    );
  }

  // InsightsPanel shows over the breadcrumb on the level frames only —
  // pulled when the user is reading a specific invoice or list, those
  // views are dense enough without a header strip too.
  // Insights panel numbers are not item-scoped — hide it under an item filter.
  const showInsights = frame.type === "level" && !filters.item;

  return (
    <InnerLayout title="Sales Breakdown">
      <div className="flex flex-col gap-4 mx-auto px-4 py-4 bg-[var(--nav-head-bg)] min-h-full">
        <Header />
        <FiltersBar value={filters} onChange={setFilters} mode={frame.type} />

        {showInsights && (
          <InsightsPanel
            constraints={frame.constraints}
            from={filters.from}
            to={filters.to}
            onDrillSales={drillFromOverallCard}
            onDrillPurchase={drillFromOverallCard}
          />
        )}

        <BreadcrumbBar
          navStack={navStack}
          onCrumb={popTo}
          onBack={back}
        />

        <Body
          frame={frame}
          state={state}
          searchTerm={filters.search}
          onDrill={drillFromRow}
          onOpenInvoices={openInvoices}
          onOpenInvoiceDetail={openInvoiceDetail}
          onOverallDrill={drillFromOverallCard}
          onRetry={reload}
        />
      </div>
    </InnerLayout>
  );
}

/* ----------------- Header ----------------- */
function Header() {
  return (
    <div className="flex items-baseline justify-between gap-3 flex-wrap">
      <div>
        <h1 className="text-[20px] font-bold text-[var(--dashboard-gray-900)] tracking-tight">
          Sales &amp; Purchase Breakdown
        </h1>
        <p className="text-[12.5px] text-[var(--dashboard-gray-500)] mt-1">
          Pan-network rollups · drill from Overall → Category → State → City → Store → Invoices.
          All figures are live CPOS aggregates.
        </p>
      </div>
    </div>
  );
}

/* ----------------- Breadcrumb ----------------- */
function BreadcrumbBar({ navStack, onCrumb, onBack }) {
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="flex items-center gap-1 flex-wrap text-[13px]">
        {navStack.map((f, idx) => {
          const last = idx === navStack.length - 1;
          return (
            <React.Fragment key={idx}>
              {idx > 0 && <ChevronRight size={12} className="text-[var(--dashboard-gray-400)]" />}
              <button
                type="button"
                disabled={last}
                onClick={() => onCrumb(idx)}
                className={`px-2.5 py-1 rounded-md font-semibold ${last
                  ? "text-[var(--dashboard-gray-900)] bg-white border border-[var(--dashboard-gray-200)] cursor-default"
                  : "text-[var(--dashboard-gray-600)] hover:text-[var(--dashboard-info)] hover:underline"
                  }`}
              >
                {f.label}
              </button>
            </React.Fragment>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onBack}
        disabled={navStack.length <= 1}
        className="flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold text-[var(--dashboard-gray-800)] bg-white border border-[var(--dashboard-gray-200)] rounded-lg hover:border-[var(--dashboard-gray-300)] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <ArrowLeft size={13} /> Back
      </button>
    </div>
  );
}

/* ----------------- Body dispatch ----------------- */
function Body({
  frame,
  state,
  searchTerm,
  onDrill,
  onOpenInvoices,
  onOpenInvoiceDetail,
  onOverallDrill,
  onRetry,
}) {
  if (state.loading) return <LoadingState />;
  if (state.error) return <ErrorState err={state.error} onRetry={onRetry} />;
  if (!state.data) return null;

  // Shape-guard: when the user clicks to drill into a new frame, the next
  // fetch hasn't started yet (reload runs in a useEffect on the next tick),
  // so state.data still belongs to the PREVIOUS frame. Render the loading
  // spinner instead of letting a child crash on `data.invoices.length`.
  if (frame.type === "level") {
    const isLevelShape = state.data && (state.data.totals || state.data.rows);
    if (!isLevelShape) return <LoadingState />;
    if (frame.level === "overall") {
      return <OverallCards totals={state.data.totals || {}} onDrill={onOverallDrill} />;
    }
    const rows = state.data.rows || [];
    if (rows.length === 0) {
      return (
        <EmptyState
          headline="No data in this scope"
          body="Adjust the date range or search to surface matching records."
        />
      );
    }
    return (
      <GroupGrid
        groupKey={frame.level}
        rows={rows}
        onDrill={onDrill}
        onOpenInvoices={frame.level === "store" ? onOpenInvoices : null}
      />
    );
  }

  if (frame.type === "invoices") {
    if (!state.data || !Array.isArray(state.data.invoices)) return <LoadingState />;
    return <InvoiceTable data={state.data} onOpen={onOpenInvoiceDetail} />;
  }

  if (frame.type === "invoiceDetail") {
    if (!state.data || !state.data.invoice) return <LoadingState />;
    return <InvoiceDetail data={state.data} searchTerm={searchTerm} />;
  }

  return null;
}

/* ----------------- States ----------------- */
function LoadingState() {
  return (
    <div className="bg-white border border-[var(--dashboard-gray-200)] rounded-xl py-16 flex flex-col items-center justify-center text-[var(--dashboard-gray-500)]">
      <Loader2 className="animate-spin text-[var(--dashboard-info)]" size={28} />
      <div className="mt-3 text-[13px]">Loading…</div>
    </div>
  );
}

function ErrorState({ err, onRetry }) {
  return (
    <div className="bg-[#FEF2F2] border border-[#FCD0D0] rounded-xl p-5 flex items-start gap-3">
      <AlertTriangle className="text-[var(--dashboard-danger)] mt-0.5" size={18} />
      <div className="flex-1">
        <div className="text-[14px] font-bold text-[var(--dashboard-danger)]">Couldn’t load this view</div>
        <div className="text-[12.5px] text-[#7F2727] mt-0.5">
          {err?.message || "Network error — please retry."}
        </div>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="px-3 py-1.5 text-[12.5px] font-semibold text-white bg-[var(--dashboard-danger)] rounded-md hover:opacity-90"
      >
        Retry
      </button>
    </div>
  );
}

function EmptyState({ headline, body }) {
  return (
    <div className="bg-white border border-dashed border-[var(--dashboard-gray-200)] rounded-xl py-14 text-center text-[var(--dashboard-gray-500)]">
      <div className="text-[26px] mb-2">🔍</div>
      <div className="text-[14px] font-bold text-[var(--dashboard-gray-900)]">{headline}</div>
      <div className="text-[12.5px] mt-1">{body}</div>
    </div>
  );
}

function NotForPos() {
  return (
    <div className="max-w-md mx-auto mt-12 bg-white border border-[var(--dashboard-gray-200)] rounded-xl p-6 text-center">
      <div className="text-[18px] font-bold text-[var(--dashboard-gray-900)]">CPOS only</div>
      <div className="text-[13px] text-[var(--dashboard-gray-500)] mt-2">
        The Sales Breakdown dashboard aggregates data across all stores, which
        is only available on the CPOS admin instance.
      </div>
    </div>
  );
}
